import React, { useCallback } from 'react';
import { Grid, Typography, Box } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { alpha, ThemeProvider } from '@mui/material/styles';

import LoginForm from '../../components/Common/LoginForm';
import { ReactComponent as SolyticsPartnersLogo } from '../../assets/SolyticsPartnersLogo.svg';
import { ReactComponent as PrismIcon } from '../../assets/PrismIcon.svg';
import SwalToast from '../../components/Common/SwalToast';
import useAuth from '../../hooks/useAuth';
import ResetPasswordSubadmin from '../../components/ResetPasswordSudadmin.js/ResetPasswordSubadmin';
import adminPanelLightTheme from '../../Theme/light';

const AdminPanelLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginRequest, forgotPasswordSubadmin } = useAuth();
  const theme = adminPanelLightTheme;

  const handleLogin = async (values) => {
    await loginRequest({ email: values?.email, password: values?.password });
    navigate('/dashboard');
  };

  const isLoginPage = location.pathname === '/login';

  const handleForgotPassword = useCallback(
    async (email) => {
      const response = await forgotPasswordSubadmin({ email });
      if (response?.status === 200) {
        SwalToast({
          icon: 'success',
          title:
            response?.data?.message ||
            'Password reset link sent to your email.',
        });
      }
    },
    [forgotPasswordSubadmin]
  );

  return (
    <ThemeProvider theme={theme}>
      <Grid
        container
        height="100vh"
        sx={{
          backgroundColor: theme.palette?.background?.default,
          zIndex: 1,
          overflowX: 'auto',
        }}
      >
        <Grid
          container
          item
          xs={12}
          sm={12}
          md={6}
          sx={{
            p: 1,
          }}
          backgroundColor="primary.main"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid
            item
            xs={8}
            display="flex"
            flexDirection="column"
            flexGrow={1}
            justifyContent="center"
            alignItems="center"
          >
            <Typography
              sx={{
                color: theme.palette.other.white,
                textAlign: 'left',
                mb: 6,
                fontSize: '2.5rem',
              }}
            >
              Streamline & transform your risk management, compliance, and data
              analytics practices
            </Typography>

            <Box
              sx={{
                position: 'relative',
                width: '100%',
                padding: 12,
                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                border: `1px solid ${theme.palette.secondary.main}`,
                borderRadius: 2,
                mt: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PrismIcon />

              <Typography
                variant="h4"
                sx={{
                  position: 'absolute',
                  top: 40,
                  left: '25%',
                  color: theme.palette.other.white,
                  transform: 'translateX(-50%)',
                  whiteSpace: 'nowrap',
                }}
              >
                Cutting Edge Tech
              </Typography>

              <Typography
                variant="h4"
                sx={{
                  position: 'absolute',
                  top: 40,
                  right: '25%',
                  transform: 'translateX(50%)',
                  color: theme.palette.other.white,
                  whiteSpace: 'nowrap',
                }}
              >
                Advanced Analytics
              </Typography>

              {/* Bottom‐Centered Label (“Deep Experties”) */}
              <Typography
                variant="h4"
                sx={{
                  position: 'absolute',
                  bottom: 40,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  color: theme.palette.other.white,
                  whiteSpace: 'nowrap',
                }}
              >
                Deep Expertise
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Grid
          container
          item
          xs={12}
          sm={12}
          md={6}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            gap: 8,
            height: '100%',
          }}
        >
          <SolyticsPartnersLogo color={theme.palette.primary.main} />
          {isLoginPage ? (
            <LoginForm
              onLoginClick={handleLogin}
              showForgotPassword={true}
              onForgotPassword={handleForgotPassword}
            />
          ) : (
            <ResetPasswordSubadmin />
          )}
        </Grid>
      </Grid>
    </ThemeProvider>
  );
};

export default AdminPanelLoginPage;
